'use client';

import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, Calendar as CalendarIcon, XCircle, CheckCircle, Ban, TrendingUp, Clock } from 'lucide-react';

import { useSubscriptionCalendarDetails } from '@/lib/api/analytics';
import { useStatsFilters } from '../context/StatsFiltersContext';
import { formatMoney } from "@/lib/money";
import { formatArm, splitByRetryIndex, splitDayPayments, type RetryIndexed } from '@/lib/subscription-retry-split';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { useTranslation } from 'react-i18next';

interface SubscriptionDetailViewProps {
  date: string;
}

export function SubscriptionDetailView({ date }: SubscriptionDetailViewProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedStores, selectedCurrency } = useStatsFilters();
  const { t, i18n } = useTranslation('analytics');

  // Fetch details for selected date
  const { data: detailedData, isLoading, isError } = useSubscriptionCalendarDetails(
    {
      date: date || '',
      storeIds: selectedStores,
      currency: selectedCurrency,
    },
    { enabled: !!date }
  );

  const selectedDateFormatted = date ? new Date(date + 'T00:00:00').toLocaleDateString(i18n.language, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }) : '';

  const getRetryLabel = (retryIndex: number) => {
    return retryIndex === 0 ? t('calendar.detail.firstAttempt') : t('calendar.detail.retry', { index: retryIndex });
  };

  const attemptSplit = splitDayPayments({
    successful: detailedData?.successful_payments?.payments,
    preauth: detailedData?.preauth_payments?.payments,
    failed: detailedData?.failed_payments?.payments,
  });

  // First attempts headline the figure, retries trail it as a dimmed secondary one
  const renderSplitCount = (payments: readonly RetryIndexed[] | undefined, retryClassName: string) => {
    const { first, retries } = splitByRetryIndex(payments);

    return (
      <>
        {first}
        {retries > 0 && <span className={retryClassName}>+{retries}</span>}
      </>
    );
  };

  const handleBack = () => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete('date');
    setSearchParams(params, { replace: true });
  };

  return (
    <div className="space-y-6 pb-8">
      {/* Back Button and Header */}
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleBack}
          className="gap-2 -ml-2"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('calendar.detail.backToCalendar')}
        </Button>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <CalendarIcon className="w-4 h-4" />
          {selectedDateFormatted}
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-10 w-10 border-3 border-gray-200 dark:border-gray-800 border-t-blue-500"></div>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('calendar.detail.loading')}</p>
          </div>
        </div>
      ) : isError ? (
        <div className="text-center py-16">
          <div className="max-w-sm mx-auto">
            <div className="w-12 h-12 rounded-full bg-rose-100 dark:bg-rose-950 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-6 h-6 text-rose-600 dark:text-rose-400" />
            </div>
            <p className="text-base font-medium text-gray-900 dark:text-gray-100 mb-1">{t('calendar.detail.errorTitle')}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('calendar.detail.errorDescription')}</p>
          </div>
        </div>
      ) : detailedData ? (
        <div className="space-y-6">
          {/* Summary Metrics - Compact Modern Design */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {/* Upcoming Billing */}
            <div className="group relative overflow-hidden rounded-lg border bg-card p-3 hover:shadow-md transition-all">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-muted-foreground mb-1">{t('calendar.upcoming')}</p>
                  <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mb-0.5">{detailedData.upcoming_billing.count}</p>
                  <p className="text-xs text-muted-foreground truncate">{formatMoney(detailedData.upcoming_billing.total_amount, selectedCurrency)}</p>
                </div>
                <div className="w-8 h-8 rounded-md bg-emerald-500/10 dark:bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                </div>
              </div>
              <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-emerald-500 to-emerald-600" />
            </div>

            {/* Successful Payments */}
            <div className="group relative overflow-hidden rounded-lg border bg-card p-3 hover:shadow-md transition-all">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-muted-foreground mb-1">{t('calendar.successful')}</p>
                  <p className="text-2xl font-bold text-sky-600 dark:text-sky-400 mb-0.5">
                    {renderSplitCount(detailedData.successful_payments.payments, 'ml-1.5 text-sm font-medium opacity-60')}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{formatMoney(detailedData.successful_payments.total_amount, selectedCurrency)}</p>
                </div>
                <div className="w-8 h-8 rounded-md bg-sky-500/10 dark:bg-sky-500/20 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-4 h-4 text-sky-600 dark:text-sky-400" />
                </div>
              </div>
              <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-sky-500 to-sky-600" />
            </div>

            {/* Failed Payments */}
            <div className="group relative overflow-hidden rounded-lg border bg-card p-3 hover:shadow-md transition-all">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-muted-foreground mb-1">{t('calendar.failed')}</p>
                  <p className="text-2xl font-bold text-rose-600 dark:text-rose-400 mb-0.5">
                    {renderSplitCount(detailedData.failed_payments.payments, 'ml-1.5 text-sm font-medium opacity-60')}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{formatMoney(detailedData.failed_payments.total_attempted, selectedCurrency)}</p>
                </div>
                <div className="w-8 h-8 rounded-md bg-rose-500/10 dark:bg-rose-500/20 flex items-center justify-center flex-shrink-0">
                  <XCircle className="w-4 h-4 text-rose-600 dark:text-rose-400" />
                </div>
              </div>
              <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-rose-500 to-rose-600" />
            </div>

            {/* PreAuth Payments */}
            <div className="group relative overflow-hidden rounded-lg border bg-card p-3 hover:shadow-md transition-all">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-muted-foreground mb-1">{t('calendar.preAuth')}</p>
                  <p className="text-2xl font-bold text-amber-600 dark:text-amber-400 mb-0.5">
                    {renderSplitCount(detailedData.preauth_payments.payments, 'ml-1.5 text-sm font-medium opacity-60')}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{formatMoney(detailedData.preauth_payments.total_amount, selectedCurrency)}</p>
                </div>
                <div className="w-8 h-8 rounded-md bg-amber-500/10 dark:bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                </div>
              </div>
              <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-amber-500 to-amber-600" />
            </div>

            {/* Planned Cancellations */}
            <div className="group relative overflow-hidden rounded-lg border bg-card p-3 hover:shadow-md transition-all">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-muted-foreground mb-1">{t('calendar.willCancel')}</p>
                  <p className="text-2xl font-bold text-violet-600 dark:text-violet-400 mb-0.5">{detailedData.planned_cancellations.count}</p>
                  <p className="text-xs text-muted-foreground">&nbsp;</p>
                </div>
                <div className="w-8 h-8 rounded-md bg-violet-500/10 dark:bg-violet-500/20 flex items-center justify-center flex-shrink-0">
                  <CalendarIcon className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                </div>
              </div>
              <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-violet-500 to-violet-600" />
            </div>

            {/* Canceled Subscriptions */}
            <div className="group relative overflow-hidden rounded-lg border bg-card p-3 hover:shadow-md transition-all">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-muted-foreground mb-1">{t('calendar.canceled')}</p>
                  <p className="text-2xl font-bold text-gray-600 dark:text-gray-400 mb-0.5">{detailedData.subscription_canceled.count}</p>
                  <p className="text-xs text-muted-foreground">&nbsp;</p>
                </div>
                <div className="w-8 h-8 rounded-md bg-gray-500/10 dark:bg-gray-500/20 flex items-center justify-center flex-shrink-0">
                  <Ban className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                </div>
              </div>
              <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-gray-500 to-gray-600" />
            </div>
          </div>

          {/* Rebill attempts, split by arm: retries share a billing cycle with the attempt they follow */}
          {attemptSplit.firstAttempt.attempts + attemptSplit.retries.attempts > 0 && (
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-muted-foreground">
              <span>
                <span className="font-medium text-foreground">{t('approvalRates.firstAttempt')}</span>{' '}
                {formatArm(attemptSplit.firstAttempt)}
              </span>
              <span>
                <span className="font-medium text-foreground">{t('calendar.retries')}</span>{' '}
                {formatArm(attemptSplit.retries)}
              </span>
            </div>
          )}

          <Separator />

          {/* Tabbed Interface */}
          <Tabs defaultValue="upcoming" className="w-full">
            <TabsList className="w-full h-auto p-1 bg-muted/40 sm:grid sm:grid-cols-6">
              <TabsTrigger
                value="upcoming"
                className="text-xs py-2.5 data-[state=active]:bg-background data-[state=active]:text-emerald-600 data-[state=active]:dark:text-emerald-400 data-[state=active]:shadow-sm"
              >
                <span className="hidden sm:inline">{t('calendar.upcoming')}</span>
                <span className="sm:hidden">{t('calendar.detail.upShort')}</span>
                {detailedData.upcoming_billing.count > 0 && (
                  <Badge variant="secondary" className="ml-1.5 px-1 text-[10px] h-4">{detailedData.upcoming_billing.count}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="successful"
                className="text-xs py-2.5 data-[state=active]:bg-background data-[state=active]:text-sky-600 data-[state=active]:dark:text-sky-400 data-[state=active]:shadow-sm"
              >
                <span className="hidden sm:inline">{t('calendar.successful')}</span>
                <span className="sm:hidden">{t('calendar.detail.winShort')}</span>
                {detailedData.successful_payments.count > 0 && (
                  <Badge variant="secondary" className="ml-1.5 px-1 text-[10px] h-4">
                    {renderSplitCount(detailedData.successful_payments.payments, 'ml-0.5 font-normal opacity-70')}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="failed"
                className="text-xs py-2.5 data-[state=active]:bg-background data-[state=active]:text-rose-600 data-[state=active]:dark:text-rose-400 data-[state=active]:shadow-sm"
              >
                <span className="hidden sm:inline">{t('calendar.failed')}</span>
                <span className="sm:hidden">{t('calendar.detail.failShort')}</span>
                {detailedData.failed_payments.count > 0 && (
                  <Badge variant="secondary" className="ml-1.5 px-1 text-[10px] h-4">
                    {renderSplitCount(detailedData.failed_payments.payments, 'ml-0.5 font-normal opacity-70')}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="preauth"
                className="text-xs py-2.5 data-[state=active]:bg-background data-[state=active]:text-amber-600 data-[state=active]:dark:text-amber-400 data-[state=active]:shadow-sm"
              >
                <span className="hidden sm:inline">{t('calendar.preAuth')}</span>
                <span className="sm:hidden">{t('calendar.detail.paShort')}</span>
                {detailedData.preauth_payments.count > 0 && (
                  <Badge variant="secondary" className="ml-1.5 px-1 text-[10px] h-4">
                    {renderSplitCount(detailedData.preauth_payments.payments, 'ml-0.5 font-normal opacity-70')}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="planned"
                className="text-xs py-2.5 data-[state=active]:bg-background data-[state=active]:text-violet-600 data-[state=active]:dark:text-violet-400 data-[state=active]:shadow-sm"
              >
                <span className="hidden sm:inline">{t('calendar.willCancel')}</span>
                <span className="sm:hidden">{t('calendar.detail.planShort')}</span>
                {detailedData.planned_cancellations.count > 0 && (
                  <Badge variant="secondary" className="ml-1.5 px-1 text-[10px] h-4">{detailedData.planned_cancellations.count}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="canceled"
                className="text-xs py-2.5 data-[state=active]:bg-background data-[state=active]:text-gray-700 data-[state=active]:dark:text-gray-300 data-[state=active]:shadow-sm"
              >
                <span className="hidden sm:inline">{t('calendar.canceled')}</span>
                <span className="sm:hidden">{t('calendar.detail.canShort')}</span>
                {detailedData.subscription_canceled.count > 0 && (
                  <Badge variant="secondary" className="ml-1.5 px-1 text-[10px] h-4">{detailedData.subscription_canceled.count}</Badge>
                )}
              </TabsTrigger>
            </TabsList>

            {/* Upcoming Billing Tab */}
            <TabsContent value="upcoming" className="mt-4">
              {detailedData.upcoming_billing?.count > 0 ? (
                <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
                  {detailedData.upcoming_billing.subscriptions.map((sub: any) => (
                    <div
                      key={sub.id}
                      className="group relative rounded-lg border bg-card p-4 hover:shadow-md transition-all"
                    >
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1 min-w-0 space-y-1">
                          <p className="font-medium text-sm truncate">{sub.customer_email}</p>
                          <p className="text-sm text-muted-foreground truncate">{sub.product_name}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-semibold text-sm">{formatMoney(sub.amount, sub.currency)}</p>
                          <p className="text-xs text-muted-foreground">
                            {sub.next_billing_date && new Date(sub.next_billing_date).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                          </p>
                        </div>
                      </div>
                      <div className="absolute inset-x-0 bottom-0 h-0.5 bg-emerald-500/20 group-hover:bg-emerald-500/40 transition-colors" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
                    <Clock className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground">{t('calendar.detail.noUpcoming')}</p>
                </div>
              )}
            </TabsContent>

            {/* Successful Payments Tab */}
            <TabsContent value="successful" className="mt-4">
              {detailedData.successful_payments?.count > 0 ? (
                <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
                  {detailedData.successful_payments.payments.map((payment: any) => (
                    <div
                      key={payment.id}
                      className="group relative rounded-lg border bg-card p-4 hover:shadow-md transition-all"
                    >
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1 min-w-0 space-y-2">
                          <p className="font-medium text-sm truncate">{payment.customer_email}</p>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <Badge variant="secondary" className="text-[10px] h-5 px-2">{payment.processor}</Badge>
                            <Badge variant="outline" className="text-[10px] h-5 px-2">{getRetryLabel(payment.retry_index)}</Badge>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-semibold text-sm">{formatMoney(payment.amount, payment.currency)}</p>
                        </div>
                      </div>
                      <div className="absolute inset-x-0 bottom-0 h-0.5 bg-sky-500/20 group-hover:bg-sky-500/40 transition-colors" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
                    <CheckCircle className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground">{t('calendar.detail.noSuccessful')}</p>
                </div>
              )}
            </TabsContent>

            {/* Failed Payments Tab */}
            <TabsContent value="failed" className="mt-4">
              {detailedData.failed_payments?.count > 0 ? (
                <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
                  {detailedData.failed_payments.payments.map((payment: any) => (
                    <div
                      key={payment.id}
                      className="group relative rounded-lg border bg-card p-4 hover:shadow-md transition-all"
                    >
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1 min-w-0 space-y-2">
                          <p className="font-medium text-sm truncate">{payment.customer_email}</p>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <Badge variant="secondary" className="text-[10px] h-5 px-2">{payment.processor}</Badge>
                            <Badge variant="outline" className="text-[10px] h-5 px-2">{getRetryLabel(payment.retry_index)}</Badge>
                          </div>
                          {payment.error_message && (
                            <p className="text-xs text-rose-600 dark:text-rose-400 font-medium mt-1">
                              {payment.status}: {payment.error_message}
                            </p>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-semibold text-sm">{formatMoney(payment.amount, payment.currency)}</p>
                        </div>
                      </div>
                      <div className="absolute inset-x-0 bottom-0 h-0.5 bg-rose-500/20 group-hover:bg-rose-500/40 transition-colors" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
                    <XCircle className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground">{t('calendar.detail.noFailed')}</p>
                </div>
              )}
            </TabsContent>

            {/* PreAuth Payments Tab */}
            <TabsContent value="preauth" className="mt-4">
              {detailedData.preauth_payments?.count > 0 ? (
                <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
                  {detailedData.preauth_payments.payments.map((payment: any) => (
                    <div
                      key={payment.id}
                      className="group relative rounded-lg border bg-card p-4 hover:shadow-md transition-all"
                    >
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1 min-w-0 space-y-2">
                          <p className="font-medium text-sm truncate">{payment.customer_email}</p>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <Badge variant="secondary" className="text-[10px] h-5 px-2">{payment.processor}</Badge>
                            <Badge variant="outline" className="text-[10px] h-5 px-2">{getRetryLabel(payment.retry_index)}</Badge>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-semibold text-sm">{formatMoney(payment.amount, payment.currency)}</p>
                        </div>
                      </div>
                      <div className="absolute inset-x-0 bottom-0 h-0.5 bg-amber-500/20 group-hover:bg-amber-500/40 transition-colors" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
                    <TrendingUp className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground">{t('calendar.detail.noPreAuth')}</p>
                </div>
              )}
            </TabsContent>

            {/* Planned Cancellations Tab */}
            <TabsContent value="planned" className="mt-4">
              {detailedData.planned_cancellations?.count > 0 ? (
                <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
                  {detailedData.planned_cancellations.subscriptions.map((sub: any) => (
                    <div
                      key={sub.id}
                      className="group relative rounded-lg border bg-card p-4 hover:shadow-md transition-all"
                    >
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1 min-w-0 space-y-1">
                          <p className="font-medium text-sm truncate">{sub.customer_email}</p>
                          <p className="text-sm text-muted-foreground truncate">{sub.product_name}</p>
                          {sub.reason && (
                            <p className="text-xs text-muted-foreground mt-1">{t('calendar.detail.reason')}: {sub.reason}</p>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-xs text-muted-foreground">{t('calendar.detail.cancels')}</p>
                          <p className="text-sm font-medium">
                            {sub.cancel_at && new Date(sub.cancel_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="absolute inset-x-0 bottom-0 h-0.5 bg-violet-500/20 group-hover:bg-violet-500/40 transition-colors" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
                    <CalendarIcon className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground">{t('calendar.detail.noWillCancel')}</p>
                </div>
              )}
            </TabsContent>

            {/* Canceled Subscriptions Tab */}
            <TabsContent value="canceled" className="mt-4">
              {detailedData.subscription_canceled?.count > 0 ? (
                <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
                  {detailedData.subscription_canceled.subscriptions.map((sub: any) => (
                    <div
                      key={sub.id}
                      className="group relative rounded-lg border bg-card p-4 hover:shadow-md transition-all"
                    >
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1 min-w-0 space-y-1">
                          <p className="font-medium text-sm truncate">{sub.customer_email}</p>
                          <p className="text-sm text-muted-foreground truncate">{sub.product_name}</p>
                          {sub.reason && (
                            <p className="text-xs text-muted-foreground mt-1">{t('calendar.detail.reason')}: {sub.reason}</p>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-xs text-muted-foreground">{t('calendar.canceled')}</p>
                          <p className="text-sm font-medium">
                            {sub.canceled_at && new Date(sub.canceled_at).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                          </p>
                        </div>
                      </div>
                      <div className="absolute inset-x-0 bottom-0 h-0.5 bg-gray-500/20 group-hover:bg-gray-500/40 transition-colors" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
                    <Ban className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground">{t('calendar.detail.noCanceled')}</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      ) : (
        <div className="text-center py-16">
          <div className="max-w-sm mx-auto">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <CalendarIcon className="w-8 h-8 text-muted-foreground" />
            </div>
            <p className="text-base font-semibold mb-1">{t('calendar.detail.noActivity')}</p>
            <p className="text-sm text-muted-foreground">{t('calendar.detail.noEvents')}</p>
          </div>
        </div>
      )}
    </div>
  );
}
